import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { HealthcareEntitiesService } from '../../../../../js/api';
import type { CustomFilterConfig, ListFilterValues } from '../../../components/list-filter';

/** URL param and API param name of the contacts list's facility filter. */
export const FACILITY_FILTER_KEY = 'facility';
export const CONTACT_CUSTOM_FILTERS = [{ key: FACILITY_FILTER_KEY }];
const FACILITY_OPTIONS_LIMIT = 500;
const FACILITY_OPTIONS_STALE_MS = 5 * 60 * 1000;
const NO_ORG_HINT = 'Choose an organization';
const CAPPED_HINT = `Only the first ${FACILITY_OPTIONS_LIMIT} facilities (by name) are listed`;
const NO_FACILITY_IDS: string[] = [];

type UseContactFacilityFilterOptions = {
  /** The resolved org: acting-as (locked) or the Organization dropdown for staff; '' for none. */
  healthcareEntity: string;
  audienceIsStaff: boolean;
  filterValues: Partial<ListFilterValues>;
  displayType: 'card' | 'table';
  updateFilters: (values: ListFilterValues) => void;
};

/**
 * The contacts list's multi-select Facility filter: options are the resolved
 * org's facilities (a rep's own org is applied server-side); staff with no org
 * get a disabled pill, since every facility across all orgs has no natural
 * bound; past FACILITY_OPTIONS_LIMIT the pill's tooltip says the set is capped.
 * Changing org drops the picks — not on mount, so a deep link carrying both
 * survives — and the list never requests the old picks against the new org.
 */
export function useContactFacilityFilter({
  healthcareEntity,
  audienceIsStaff,
  filterValues,
  displayType,
  updateFilters,
}: UseContactFacilityFilterOptions) {
  const orgResolved = !audienceIsStaff || Boolean(healthcareEntity);
  // Between an org change and the effect below clearing the URL, one render still
  // carries the old org's picks: treat them as empty so the list never requests
  // "new org + old facilities".
  const previousOrgRef = useRef(healthcareEntity);
  const orgChanged = previousOrgRef.current !== healthcareEntity;
  const facilityIds = orgChanged
    ? NO_FACILITY_IDS
    : (filterValues.customFilters?.[FACILITY_FILTER_KEY] ?? NO_FACILITY_IDS);

  const { data } = useQuery({
    queryKey: ['contacts-facility-filter-options', healthcareEntity],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesList({
        healthcareEntity: healthcareEntity || undefined,
        ordering: 'name',
        limit: FACILITY_OPTIONS_LIMIT,
      }),
    enabled: orgResolved,
    staleTime: FACILITY_OPTIONS_STALE_MS,
  });

  const customFilters = useMemo(
    (): CustomFilterConfig[] => [
      {
        key: FACILITY_FILTER_KEY,
        label: 'Facility',
        options: (data?.results ?? []).map((facility) => ({
          value: facility.id,
          label: facility.name || '--',
          is_active: true,
        })),
        disabled: !orgResolved,
        disabledHint: NO_ORG_HINT,
        hint: data?.next ? CAPPED_HINT : undefined,
      },
    ],
    [data, orgResolved]
  );

  const stalePicks = filterValues.customFilters?.[FACILITY_FILTER_KEY];
  useEffect(() => {
    if (!orgChanged) return;
    previousOrgRef.current = healthcareEntity;
    if (stalePicks && stalePicks.length > 0) {
      updateFilters({ ...filterValues, displayType, customFilters: undefined });
    }
  }, [orgChanged, healthcareEntity, stalePicks, filterValues, displayType, updateFilters]);

  return { facilityIds, customFilters };
}
